import React, { LegacyRef, ReactNode, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import {
    ConfirmationModal,
    ConfirmationModalProps,
    useModal,
} from '@europrocurement/flexy-components';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { useFileUploadProps } from '@europrocurement/l2d-modules/modules/ParcoursFormalite/hooks/useFileUploadProps';
import DocumentItem from '../DocumentItem/DocumentItem';
import UploadableDocument from '../UploadableDocument/UploadableDocument';
import { DocumentAction } from '../DocumentActions/DocumentActions';
import { Document } from '../../../providers/types';
import { DossierFichier } from '../../../hooks';

export type RequiredDocumentItemProps = {
    onChange: (files: FileList) => void;
    onDeletion: () => void;
    onDownload: () => void;
    onShow: () => void;
    onMove: () => void;
    onClick: () => void;
    uploadable: boolean;
    uploading: boolean;
    editable: boolean;
    showable: boolean;
    movable: boolean;
    downloadable: boolean;
    removable: boolean;
    showUploadBtn: boolean;
    smallUploadableBtn?: boolean;
    customAction?: ReactNode;
    file?: DossierFichier;
} & Document;

const RequiredDocumentItem: React.FunctionComponent<RequiredDocumentItemProps> = function (props) {
    const {
        onChange: handleChange,
        onDeletion: handleDeletion,
        onDownload: handleDownload,
        onShow: handleShow,
        onClick: handleClick,
        onMove: handleMove,
        uploadable,
        uploading,
        editable,
        showable,
        downloadable,
        removable,
        movable,
        showUploadBtn,
        smallUploadableBtn,
        customAction,
        label,
        fileName,
        type,
        status,
        feedback,
        file,
    } = props;
    const { palette } = useTheme();
    const [isMouseHover, setIsMouseHover] = useState<number>(0);
    const [isDragHover, setIsDragHover] = useState<number>(0);
    const fileInputRef: LegacyRef<HTMLLabelElement> = useRef(null);
    const { accept, maxMbSize } = useFileUploadProps();

    const handleMouseEnter = () => setIsMouseHover((val) => val + 1);
    const handleMouseLeave = () => setIsMouseHover((val) => val - 1);
    const handleDragEnter = () => setIsDragHover((val) => val + 1);
    const handleDragLeave = () => setIsDragHover((val) => val - 1);
    const handleValidationError = (err: Error) => {
        enqueueSnackbar(<Typography>{err.message}</Typography>, { variant: 'error' });
    };

    const actions: Array<DocumentAction> = [];
    const { modalActions } = useModal();
    const modalProps: ConfirmationModalProps = {
        messages: {
            question: 'Supprimer ce document ?',
            detail: {
                message: 'Êtes vous sur de vouloir supprimer ce document ?',
                severity: 'info',
            },
        },
        actionOnValidation: () => {
            modalActions.close();
            handleDeletion();
        },
        actionOnCancellation: () => {
            modalActions.close();
        },
        color: palette.warning,
    };

    const actionIconSx = {
        fontWeight: '400',
        fontSize: '16px',
        lineHeight: '16px',
        ':hover': { color: 'text.secondary' },
    };

    if (showable) {
        actions.push({
            title: 'Afficher le document',
            content: (
                <FaOptionIcon
                    icon={['fasl', 'eye']}
                    color="text.gris35"
                    sx={actionIconSx}
                />
            ),
            onClick: (e) => {
                if (e) {
                    e.preventDefault();
                    e.stopPropagation();
                }
                handleShow();
            },
        });
    }

    if (downloadable) {
        actions.push({
            title: 'Télécharger le document',
            content: (
                <FaOptionIcon
                    icon={['fasl', 'arrow-down-to-line']}
                    color="text.gris35"
                    sx={actionIconSx}
                />
            ),
            onClick: () => {
                handleDownload();
            },
        });
    }

    if (editable) {
        actions.push({
            title: 'Modifier le document',
            content: (
                <FaOptionIcon
                    icon={['fasl', 'pen']}
                    color="text.gris35"
                    sx={actionIconSx}
                />
            ),
            onClick: () => {
                fileInputRef.current?.click();
            },
        });
    }

    if (movable) {
        actions.push({
            title: 'Déplacer le document',
            content: (
                <FaOptionIcon
                    icon={['fasl', 'up-down']}
                    color="text.gris35"
                    sx={actionIconSx}
                />
            ),
            onClick: () => {
                handleMove();
            },
        });
    }

    if (removable) {
        actions.push({
            title: 'Supprimer le document',
            content: (
                <FaOptionIcon
                    icon={['fasl', 'trash-can']}
                    color="text.gris35"
                    sx={actionIconSx}
                />
            ),
            onClick: () => {
                modalActions.call(<ConfirmationModal {...modalProps} />);
            },
        });
    }

    const item = (
        <DocumentItem
            label={label as string}
            fileName={fileName}
            type={type}
            status={status}
            feedback={feedback}
            hover={isMouseHover > 0}
            highlight={isDragHover > 0}
            uploadable={uploadable}
            uploading={uploading}
            smallUploadableBtn={smallUploadableBtn}
            fileInputRef={fileInputRef}
            actions={actions}
            showUploadBtn={showUploadBtn}
            customAction={customAction}
            sx={{ cursor: 'pointer' }}
            file={file}
        />
    );

    if (!uploadable) {
        return (
            <Box
                onClick={() => handleClick()}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {item}
            </Box>
        );
    }

    return (
        <UploadableDocument
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onValidationError={handleValidationError}
            onChange={handleChange}
            accept={accept}
            maxMbSize={maxMbSize}
            fileInputRef={fileInputRef}
        >
            {item}
        </UploadableDocument>
    );
};

export default RequiredDocumentItem;
